import {ProductControlSideHeader} from '@/components/Product/ProductControlSideHeader';
import {useListes} from '@/haligator-factory';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {Box, HStack, Text} from '@chakra-ui/react';
import {useHalCollection} from '@synako/haligator';
import {Link, useParams} from 'react-router-dom';
import {ListeSection} from './components/ListeSection';
import {routes} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useTranslation} from 'react-i18next';
import {State} from '@synako/halitrek';
import {ListeDesc} from 'api-types/ffconnect-hal';
import {ErrorBoundary} from 'react-error-boundary';

/**
 * Page de détail d'une liste de produits.
 * Affiche le nom de la liste, le nombre de produits et les articles groupés par catégorie web.
 */
export const ListeView: React.FC = () => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();
  const {code_liste} = useParams<{code_liste: string}>();
  const listesHook = useHalCollection(useListes());

  // selection de la liste concernée
  const selectedListState = listesHook.items?.find((list) => {
    if (code_liste && list.data.code_liste) {
      return list.data.code_liste === code_liste;
    }
    return false;
  });

  // Composant interne qui calcule et affiche le nombre d'items de la liste
  const NombreProduits: React.FC<{liste?: State<ListeDesc>}> = ({liste}) => {
    if (!liste) return null;
    const items = liste.followAllOrThrow('selection');
    return (
      <Text
        fontSize='sm'
        whiteSpace='nowrap'
      >
        {t('global.produit', {count: items.length})}
      </Text>
    );
  };

  // Composant interne qui calcule la selection et rend la section des produits
  const ListeProduitsSection: React.FC<{liste?: State<ListeDesc>}> = ({
    liste,
  }) => {
    if (!liste) return null;
    const items = liste.followAllOrThrow('selection');
    return (
      <ListeSection
        produits={items}
        liste={liste}
        tri_selection={liste.data.tri_selection}
      />
    );
  };

  return (
    <Box
      backgroundColor='white'
      px={{base: '2', md: '4', lg: '6'}}
      py='4'
    >
      {/* Retour */}
      <Link to={buildClientRoute(routes.client.listes_default)}>
        <HStack
          spacing={1}
          color='gray.dark'
          _hover={{color: 'blue.main'}}
          mb='2'
        >
          <ChevronLeftIcon
            boxSize='5'
            color='gray.500'
          />
          <Text
            fontSize='sm'
            color='gray.500'
          >
            {t('global.retour')}
          </Text>
        </HStack>
      </Link>
      {/* Titre + bouton ajout */}
      <Box
        display='flex'
        flexDirection={{base: 'column', md: 'row'}}
        justifyContent='space-between'
        alignItems={{base: 'flex-start', md: 'center'}}
        w='100%'
      >
        <Text
          fontSize='2xl'
          fontWeight='bold'
        >
          {selectedListState?.data.nom_liste}
        </Text>
        <ProductControlSideHeader />
      </Box>
      {/* Nombre de produits */}
      <Box
        p='5px 10px'
        bg='gray.light'
        borderRadius='8px'
        w='fit-content'
        mt='2'
        fontSize='sm'
        color='gray.dark'
      >
        <ErrorBoundary fallbackRender={() => null}>
          <NombreProduits liste={selectedListState} />
        </ErrorBoundary>
      </Box>
      <Box
        border='1px solid'
        borderColor='gray.light'
        borderRadius='12px'
        boxShadow='md'
        mt='4'
        overflow='hidden'
      >
        <ErrorBoundary fallbackRender={() => null}>
          <ListeProduitsSection liste={selectedListState} />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
